import { useMemo, useState } from 'react'
import { RefreshCw, Plus, Trash2, Play } from 'lucide-react'
import { toast } from 'sonner'
import { TokenChipField } from '@/components/authoring'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import {
  PageHeaderActionSecondary,
  PageHeaderActions,
} from '@/components/navigation/page-header-actions'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { getOpportunityIngestProviders, getPageHeader } from '@/constants'
import {
  useIngestMutations,
  useIngestSearchTerms,
  useLexiconSelection,
  type IngestRunStatus,
  type LexiconSelection,
  type LexiconSelectionUpdate,
} from '@/hooks/use-marketplace-ingest-admin'
import { useSkills } from '@/hooks/use-skills'
import { useSkillDomains, type SkillDomainSummary } from '@/hooks/use-evaluation-rubrics-admin'
import { useAdminBasePath, adminPath } from '@/components/admin/use-admin-base-path'
import { formatMarketplaceProviderLabel } from '@/lib/opportunity-listing-providers'
import { publishedOnly } from '@/utils/skill-lexicon'

const TERMINAL_STATUSES = new Set(['completed', 'failed'])

function isActiveRun(status: string): boolean {
  return status === 'pending' || status === 'running'
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Prefer nested `providers` map; else treat top-level keys as per-provider slices. */
function providerStatsMap(stats: Record<string, unknown> | undefined): Record<string, unknown> | null {
  if (!stats) return null
  const nested = stats.providers
  if (isPlainRecord(nested)) return nested
  const keys = Object.keys(stats).filter((key) => isPlainRecord(stats[key]))
  if (keys.length === 0) return null
  // Avoid treating a flat Adzuna-era stats object as multi-provider.
  if (keys.every((key) => ['ok', 'error', 'errors', 'created', 'updated', 'expired', 'fetched'].includes(key))) {
    return null
  }
  return Object.fromEntries(keys.map((key) => [key, stats[key]]))
}

function ProviderStatChips({ stats }: { stats: Record<string, unknown> | undefined }) {
  const byProvider = providerStatsMap(stats)
  if (!byProvider) {
    if (!stats || Object.keys(stats).length === 0) return null
    return (
      <pre className="mt-2 overflow-x-auto text-xs text-muted-foreground">
        {JSON.stringify(stats, null, 2)}
      </pre>
    )
  }

  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {Object.entries(byProvider).map(([provider, slice]) => {
        const row = isPlainRecord(slice) ? slice : {}
        const ok = row.ok !== false && !row.error
        const created = typeof row.created === 'number' ? row.created : undefined
        const updated = typeof row.updated === 'number' ? row.updated : undefined
        const expired = typeof row.expired === 'number' ? row.expired : undefined
        const parts = [
          created !== undefined ? `${created} new` : null,
          updated !== undefined ? `${updated} updated` : null,
          expired !== undefined ? `${expired} expired` : null,
        ].filter(Boolean)
        return (
          <Badge key={provider} variant={ok ? 'successSoft' : 'destructiveSoft'} className="font-normal">
            {formatMarketplaceProviderLabel(provider)}
            {parts.length ? `: ${parts.join(', ')}` : ok ? ': ok' : ': failed'}
          </Badge>
        )
      })}
    </div>
  )
}

function domainToken(
  domain: { label: string; key: string },
  peers: { label: string; key: string }[],
): string {
  const collision = peers.filter((item) => item.label === domain.label).length > 1
  return collision ? `${domain.label} (${domain.key})` : domain.label
}

function stringList(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string')
}

function LexiconIngestSection({
  selection,
  isLoading,
  error,
  updateLexicon,
}: {
  selection: LexiconSelection | undefined
  isLoading: boolean
  error: unknown
  updateLexicon: ReturnType<typeof useIngestMutations>['updateLexicon']
}) {
  const { skills, isLoading: skillsLoading } = useSkills()
  const { data: domains = [], isLoading: domainsLoading } = useSkillDomains('employment')
  const publishedDomains = useMemo(() => publishedOnly(domains), [domains])

  const selectedSkillIds = selection?.selected_skills.map((skill) => skill.id) ?? []
  const selectedDomainIds = selection?.selected_domains.map((domain) => domain.id) ?? []
  const excludedIds = selection?.excluded_skills.map((skill) => skill.id) ?? []

  const domainTokens = (selection?.selected_domains ?? []).map((domain) =>
    domainToken(domain, publishedDomains),
  )
  const skillTokens = (selection?.selected_skills ?? []).map((skill) => skill.name)

  async function persist(next: LexiconSelectionUpdate) {
    try {
      await updateLexicon.mutateAsync(next)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update lexicon picks')
    }
  }

  function payload(overrides: Partial<LexiconSelectionUpdate>): LexiconSelectionUpdate {
    return {
      selected_skill_ids: selectedSkillIds,
      selected_domain_ids: selectedDomainIds,
      excluded_skill_ids: excludedIds,
      ...overrides,
    }
  }

  const busy = !selection || updateLexicon.isPending
  const searchedKeywords = stringList(selection?.last_sync_stats?.keywords)
  const dedupedKeywords = stringList(selection?.last_sync_stats?.deduped)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Skill lexicon</CardTitle>
        <CardDescription alwaysShow>
          Published skills you pick, and published skills in the domains you pick, are searched
          with the defaults below. Draft skills and draft domains are left out. The same wording
          is searched once. An exclusion only drops a skill that came from a domain.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground" role="status">
            Loading lexicon picks…
          </p>
        ) : null}
        {error ? (
          <p className="text-sm text-destructive" role="status">
            {error instanceof Error ? error.message : 'Could not load lexicon picks'}
          </p>
        ) : null}
        {selection ? (
          <>
            <p className="text-xs text-muted-foreground">
              {selection.country_code} · {selection.where} · max {selection.max_pages} pages ·{' '}
              {selection.max_days_old}d window
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="ingest-lexicon-domains">Domains</Label>
              <TokenChipField
                id="ingest-lexicon-domains"
                tokens={domainTokens}
                allowCreate={false}
                suggestions={publishedDomains
                  .filter((domain) => !selectedDomainIds.includes(domain.id))
                  .map((domain) => ({
                    name: domainToken(domain, publishedDomains),
                    hint: domain.key,
                  }))}
                suggestionsLoading={domainsLoading}
                disabled={busy}
                placeholder="Search published domains"
                helperText="Each published skill in a selected domain is searched."
                emptySuggestionsLabel="No published domains match."
                onAdd={(token) => {
                  const domain = publishedDomains.find(
                    (item) => domainToken(item, publishedDomains) === token,
                  )
                  if (!domain || selectedDomainIds.includes(domain.id)) return
                  void persist(payload({ selected_domain_ids: [...selectedDomainIds, domain.id] }))
                }}
                onRemove={(token) => {
                  const domain = selection.selected_domains.find(
                    (item) => domainToken(item, publishedDomains) === token,
                  )
                  if (!domain) return
                  void persist(
                    payload({
                      selected_domain_ids: selectedDomainIds.filter((id) => id !== domain.id),
                    }),
                  )
                }}
              />
            </div>
            {selection.selected_domains.map((domain) => (
              <SelectedDomainSkills
                key={domain.id}
                domain={domain}
                catalog={publishedDomains.find((item) => item.id === domain.id)}
                catalogLoading={domainsLoading}
                excludedIds={excludedIds}
                selectedSkillIds={selectedSkillIds}
                disabled={busy}
                onExclude={(skillId, exclude) => {
                  const next = exclude
                    ? [...excludedIds, skillId]
                    : excludedIds.filter((id) => id !== skillId)
                  void persist(payload({ excluded_skill_ids: next }))
                }}
              />
            ))}
            <div className="space-y-1.5">
              <Label htmlFor="ingest-lexicon-skills">Skills</Label>
              <TokenChipField
                id="ingest-lexicon-skills"
                tokens={skillTokens}
                allowCreate={false}
                suggestions={skills
                  .filter((skill) => !selectedSkillIds.includes(skill.id))
                  .map((skill) => ({ name: skill.name }))}
                suggestionsLoading={skillsLoading}
                disabled={busy}
                placeholder="Search published skills"
                helperText="A skill selected here is searched even when a domain excludes it."
                emptySuggestionsLabel="No published skills match."
                onAdd={(name) => {
                  const skill = skills.find((item) => item.name === name)
                  if (!skill || selectedSkillIds.includes(skill.id)) return
                  void persist(payload({ selected_skill_ids: [...selectedSkillIds, skill.id] }))
                }}
                onRemove={(name) => {
                  const skill = selection.selected_skills.find((item) => item.name === name)
                  if (!skill) return
                  void persist(
                    payload({
                      selected_skill_ids: selectedSkillIds.filter((id) => id !== skill.id),
                    }),
                  )
                }}
              />
            </div>
            {selection.last_sync_at ? (
              <div className="text-xs text-muted-foreground">
                <p>
                  Last lexicon sync {new Date(selection.last_sync_at).toLocaleString()} —{' '}
                  <Badge variant={selection.last_sync_status === 'ok' ? 'successSoft' : 'destructiveSoft'}>
                    {selection.last_sync_status || 'unknown'}
                  </Badge>
                </p>
                {searchedKeywords.length ? (
                  <p className="mt-1">Searched from the lexicon: {searchedKeywords.join(', ')}</p>
                ) : null}
                {dedupedKeywords.length ? (
                  <p className="mt-1">
                    Already covered by an extra term: {dedupedKeywords.join(', ')}
                  </p>
                ) : null}
                <ProviderStatChips stats={selection.last_sync_stats} />
              </div>
            ) : null}
          </>
        ) : null}
      </CardContent>
    </Card>
  )
}

function SelectedDomainSkills({
  domain,
  catalog,
  catalogLoading,
  excludedIds,
  selectedSkillIds,
  disabled,
  onExclude,
}: {
  domain: LexiconSelection['selected_domains'][number]
  catalog: SkillDomainSummary | undefined
  catalogLoading: boolean
  excludedIds: string[]
  selectedSkillIds: string[]
  disabled: boolean
  onExclude: (skillId: string, exclude: boolean) => void
}) {
  const draftDomain = domain.publication_status === 'draft'
  const skills = publishedOnly(catalog?.skills ?? [])
  const draftSkillCount = (catalog?.skills ?? []).filter(
    (skill) => skill.publication_status === 'draft',
  ).length

  return (
    <div className="rounded-md border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-medium">{domain.label}</p>
        {draftDomain ? <Badge variant="secondary">Draft — not searched</Badge> : null}
      </div>
      {draftDomain ? (
        <p className="mt-2 text-sm text-muted-foreground">Draft domains are left out of ingest.</p>
      ) : catalogLoading && !catalog ? (
        <p className="mt-2 text-sm text-muted-foreground" role="status">
          Loading skills…
        </p>
      ) : skills.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">No published skills in this domain.</p>
      ) : (
        <ul className="mt-2 space-y-2">
          {skills.map((skill) => {
            const excluded = excludedIds.includes(skill.id)
            const direct = selectedSkillIds.includes(skill.id)
            return (
              <li key={skill.id} className="flex flex-wrap items-center gap-2">
                <span className="text-sm">{skill.name}</span>
                {excluded ? <Badge variant="outline">Excluded</Badge> : null}
                {direct ? <Badge variant="outline">Selected directly</Badge> : null}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={disabled}
                  aria-label={
                    excluded ? `Include ${skill.name} from ${domain.label}` : `Exclude ${skill.name} from ${domain.label}`
                  }
                  onClick={() => onExclude(skill.id, !excluded)}
                >
                  {excluded ? 'Include' : 'Exclude'}
                </Button>
                {excluded && direct ? (
                  <span className="text-xs text-muted-foreground">
                    Still searched because this skill is selected directly.
                  </span>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
      {draftSkillCount > 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">
          {draftSkillCount === 1
            ? '1 draft skill in this domain is left out.'
            : `${draftSkillCount} draft skills in this domain are left out.`}
        </p>
      ) : null}
    </div>
  )
}

export function AdminMarketplaceIngestPage() {
  const basePath = useAdminBasePath()
  const { data: terms = [], isLoading, refetch, isFetching } = useIngestSearchTerms()
  const lexicon = useLexiconSelection()
  const { createTerm, updateTerm, deleteTerm, runSync, pollRun, updateLexicon } = useIngestMutations()
  const [keyword, setKeyword] = useState('')
  const [runStatus, setRunStatus] = useState<IngestRunStatus | null>(null)
  const enabledProviders = getOpportunityIngestProviders()

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = keyword.trim()
    if (!trimmed) return
    await createTerm.mutateAsync({ keyword: trimmed, enabled: true, max_pages: 2, max_days_old: 30 })
    setKeyword('')
  }

  async function handleRunSync(termId?: string) {
    setRunStatus(null)
    try {
      const accepted = await runSync.mutateAsync(termId)
      let status = await pollRun.mutateAsync(accepted.run_id)
      setRunStatus(status)
      const deadline = Date.now() + 120_000
      while (isActiveRun(status.status) && Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 1500))
        status = await pollRun.mutateAsync(accepted.run_id)
        setRunStatus(status)
      }
      if (status.status === 'failed') {
        toast.error(status.error || 'Sync failed')
      } else if (status.status === 'completed') {
        toast.success('Sync completed')
      }
      void refetch()
      void lexicon.refetch()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sync failed'
      toast.error(message)
    }
  }

  const header = getPageHeader(
    adminPath(basePath, 'marketplace-ingest'),
    'Marketplace ingest',
    'Search published skills and domains, plus any extra keywords, across enabled marketplace providers.',
  )

  return (
    <PageContainer>
      <PageHeader
        leading={<PageHeaderLeading />}
        title={header.title}
        description={header.description}
        actions={
          <PageHeaderActions
            secondary={
              <PageHeaderActionSecondary
                onClick={() => void handleRunSync()}
                disabled={runSync.isPending}
              >
                <Play className="h-4 w-4" aria-hidden />
                Run all enabled
              </PageHeaderActionSecondary>
            }
          />
        }
      />

      {enabledProviders.length > 0 ? (
        <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">Enabled providers</span>
          {enabledProviders.map((provider) => (
            <Badge key={provider} variant="outline">
              {formatMarketplaceProviderLabel(provider)}
            </Badge>
          ))}
        </div>
      ) : null}

      <LexiconIngestSection
        selection={lexicon.data}
        isLoading={lexicon.isLoading}
        error={lexicon.error}
        updateLexicon={updateLexicon}
      />

      <Card>
        <CardHeader>
          <CardTitle>Extra terms</CardTitle>
          <CardDescription alwaysShow>
            Free-text keywords, searched as written. Seeded terms stay until you remove them.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form className="flex flex-wrap gap-2" onSubmit={(e) => void handleCreate(e)}>
            <Input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Extra term (e.g. sustainability analyst)"
              aria-label="Extra term"
              className="max-w-md"
            />
            <Button type="submit" disabled={createTerm.isPending || !keyword.trim()}>
              <Plus className="mr-2 h-4 w-4" aria-hidden />
              Add term
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => void refetch()}
              disabled={isFetching}
              aria-label="Refresh search terms"
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} aria-hidden />
            </Button>
          </form>

          {runStatus ? (
            <div className="rounded-md border bg-muted/40 p-3 text-sm" role="status">
              <p className="font-medium">
                Last sync run: {runStatus.status}
                {TERMINAL_STATUSES.has(runStatus.status) ? '' : ' (in progress)'}
              </p>
              <ProviderStatChips stats={runStatus.stats} />
              {runStatus.error ? <p className="mt-2 text-destructive">{runStatus.error}</p> : null}
            </div>
          ) : null}

          {isLoading ? (
            <p className="text-sm text-muted-foreground" role="status">Loading search terms…</p>
          ) : terms.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No extra terms yet. Add a keyword to search it as written, alongside the skill lexicon.
            </p>
          ) : (
            <div className="space-y-3">
              {terms.map((term) => (
                <div key={term.id} className="flex flex-wrap items-center gap-3 rounded-md border p-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{term.keyword}</p>
                    <p className="text-xs text-muted-foreground">
                      {term.country_code} · {term.where} · max {term.max_pages} pages · {term.max_days_old}d window
                    </p>
                    {term.last_sync_at ? (
                      <p className="text-xs text-muted-foreground mt-1">
                        Last sync {new Date(term.last_sync_at).toLocaleString()} —{' '}
                        <Badge variant={term.last_sync_status === 'ok' ? 'successSoft' : 'destructiveSoft'}>
                          {term.last_sync_status || 'unknown'}
                        </Badge>
                      </p>
                    ) : null}
                    <ProviderStatChips stats={term.last_sync_stats} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      id={`ingest-enabled-${term.id}`}
                      checked={term.enabled}
                      onCheckedChange={(enabled) => void updateTerm.mutateAsync({ id: term.id, enabled })}
                      aria-label={`Enable ${term.keyword}`}
                    />
                    <Label htmlFor={`ingest-enabled-${term.id}`} className="text-xs">
                      Enabled
                    </Label>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => void handleRunSync(term.id)} disabled={runSync.isPending}>
                    Sync
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => void deleteTerm.mutateAsync(term.id)}
                    disabled={deleteTerm.isPending}
                    aria-label={`Delete ${term.keyword}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  )
}
